import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  LayoutDashboard, Users, Building2, Mail, Webhook,
  BarChart3, LogOut, Menu, MessageSquare, UserCog, ChevronRight, ExternalLink
} from "lucide-react";

const AVA_HEADSHOT = "https://customer-assets.emergentagent.com/job_app-constructor-85/artifacts/ngv4sm7y_AvA%20Headshot.png";

const navItems = [
  { label: "Dashboard", icon: LayoutDashboard, to: "/admin/dashboard" },
  { label: "AvA Sessions", icon: MessageSquare, to: "/admin/sessions" },
  { label: "Leads", icon: Users, to: "/admin/dashboard", match: ["/admin/dashboard", "/admin/leads"] },
  { label: "Vendor Partners", icon: Building2, to: "/admin/vendors" },
  { label: "Email Templates", icon: Mail, to: "/admin/templates" },
  { label: "Webhooks", icon: Webhook, to: "/admin/webhooks" },
  { label: "Site Metrics", icon: BarChart3, to: "/admin/metrics" },
  { label: "Team", icon: UserCog, to: "/admin/team" },
];

export default function AdminLayout({ children, title }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => { logout(); navigate("/admin"); };

  const isActive = (item) => {
    const paths = item.match || [item.to];
    return paths.some(p => location.pathname === p || location.pathname.startsWith(p + "/"));
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Gradient Header */}
      <div className="bg-gradient-to-br from-blue-600 via-blue-500 to-cyan-500 px-4 py-5">
        <Link to="/" className="flex items-center gap-3">
          <div className="relative flex-shrink-0">
            <div className="w-10 h-10 rounded-full ring-2 ring-white/30 overflow-hidden">
              <img src={AVA_HEADSHOT} alt="AvA" className="w-full h-full object-cover object-top" />
            </div>
            <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-emerald-400 border-2 border-blue-600" />
          </div>
          <div>
            <div className="text-white font-semibold text-sm leading-tight">Apexum Partners</div>
            <div className="text-white/70 text-xs">AvA Admin Dashboard</div>
          </div>
        </Link>
      </div>

      {/* Acronym tag */}
      <div className="px-4 py-2.5 bg-blue-50 border-b border-blue-100">
        <p className="text-xs text-blue-600 font-medium">AvA — Apexum's Verified Access</p>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
        {navItems.map(item => {
          const Icon = item.icon;
          const active = isActive(item);
          return (
            <Link key={item.label} to={item.to}
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                active
                  ? "bg-gradient-to-r from-blue-600 to-cyan-500 text-white shadow-md shadow-blue-600/20"
                  : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
              }`}>
              <Icon size={17} className={active ? "text-white" : ""} />
              <span className="flex-1">{item.label}</span>
              {active && <ChevronRight size={13} className="text-white/70" />}
            </Link>
          );
        })}
      </nav>

      {/* User section */}
      <div className="px-4 py-4 border-t border-slate-100">
        <div className="flex items-center gap-3 mb-3 p-2.5 rounded-xl bg-slate-50">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center flex-shrink-0">
            <span className="text-white text-xs font-semibold">
              {user?.name?.charAt(0) || user?.email?.charAt(0) || "A"}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-slate-900 truncate">{user?.name || "Admin"}</p>
            <p className="text-xs text-slate-400 truncate">{user?.email}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Link to="/" target="_blank"
            className="flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs text-slate-500 hover:text-blue-600 border border-slate-200 rounded-lg hover:border-blue-200 transition-all">
            <ExternalLink size={12} /> Public Site
          </Link>
          <button onClick={handleLogout}
            className="flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs text-slate-500 hover:text-red-600 border border-slate-200 rounded-lg hover:border-red-100 transition-all">
            <LogOut size={12} /> Sign out
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-slate-50">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col w-64 bg-white border-r border-slate-200 flex-shrink-0">
        <SidebarContent />
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="fixed inset-0 bg-black/40" onClick={() => setSidebarOpen(false)} />
          <aside className="relative flex flex-col w-64 bg-white shadow-2xl z-10">
            <SidebarContent />
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <header className="bg-white border-b border-slate-100 px-4 lg:px-6 py-3.5 flex items-center gap-4">
          <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-1.5 text-slate-500 hover:text-slate-700 rounded-lg hover:bg-slate-50">
            <Menu size={20} />
          </button>
          <h1 className="text-base font-semibold text-slate-900 flex-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{title}</h1>
        </header>
        <main className="flex-1 overflow-auto p-4 lg:p-6">
          {children}
        </main>
      </div>
    </div>
  );
}
